import React, { useEffect, useMemo, useRef, useState } from 'react';
import { matchesSearch } from '../utils/searchFilter';

/** Multi-select dropdown with type-to-search. value/onChange use string[]. */
export default function MultiSearchableSelect({
  id,
  value = [],
  onChange,
  options,
  placeholder = 'Search and select…',
  disabled,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const rootRef = useRef(null);
  const selectedSet = useMemo(() => new Set(value), [value]);

  const filtered = useMemo(
    () => options.filter((o) => matchesSearch(filter, [o.label, o.value])),
    [options, filter]
  );

  const selectedLabels = useMemo(
    () =>
      value
        .map((v) => options.find((o) => o.value === v)?.label || v)
        .filter(Boolean),
    [value, options]
  );

  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setFilter('');
      }
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = (v) => {
    if (selectedSet.has(v)) {
      onChange(value.filter((x) => x !== v));
    } else {
      onChange([...value, v]);
    }
  };

  return (
    <div className={`mps-searchable-select mps-multi-select ${className}`.trim()} ref={rootRef}>
      <button
        type="button"
        id={id}
        className="mps-searchable-select-trigger"
        disabled={disabled}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={selectedLabels.length ? '' : 'mps-muted'}>
          {selectedLabels.length ? `${selectedLabels.length} selected` : placeholder}
        </span>
        <span className="mps-searchable-select-chevron" aria-hidden>
          ▾
        </span>
      </button>
      {selectedLabels.length > 0 ? (
        <div className="mps-multi-select-chips">
          {value.map((v) => {
            const label = options.find((o) => o.value === v)?.label || v;
            return (
              <span key={v} className="mps-chip">
                {label}
                <button type="button" className="mps-chip-remove" onClick={() => toggle(v)} aria-label={`Remove ${label}`}>
                  ×
                </button>
              </span>
            );
          })}
        </div>
      ) : null}
      {open ? (
        <div className="mps-searchable-select-panel" role="listbox">
          <input
            type="text"
            className="mps-searchable-select-input"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={placeholder}
            autoComplete="off"
            autoFocus
          />
          <ul className="mps-searchable-select-list">
            {filtered.length === 0 ? (
              <li className="mps-searchable-select-empty mps-muted">No matches</li>
            ) : (
              filtered.map((o) => (
                <li key={o.value}>
                  <label className="mps-multi-select-option">
                    <input type="checkbox" checked={selectedSet.has(o.value)} onChange={() => toggle(o.value)} />
                    <span>{o.label}</span>
                  </label>
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
